import { useState } from 'react';
import { useTranslation } from '../../i18n';

/**
 * Rating Modal for Virtual Classroom
 * Students rate the lesson after it ends
 */
const RatingModal = ({
  isOpen,
  onClose,
  onSubmit,
  onSkip,
}) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit({ rating, comment });
    setRating(0);
    setComment('');
  };

  const handleSkip = () => {
    onSkip();
    setRating(0);
    setComment('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center sm:items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 max-w-md w-full my-auto max-h-[calc(100vh-2rem)] overflow-y-auto">
        <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-900 mb-2">{t('classroom.ratingTitle')}</h2>
        <p className="text-center text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">{t('classroom.ratingSubtitle')}</p>

        {/* Star Rating */}
        <div className="flex justify-center gap-1.5 sm:gap-2 mb-4 sm:mb-6">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              className="transition-transform hover:scale-110"
            >
              <svg
                className={`w-10 h-10 sm:w-12 sm:h-12 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          ))}
        </div>

        {/* Comment */}
        <div className="mb-4 sm:mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('classroom.ratingCommentLabel')}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('classroom.ratingCommentPlaceholder')}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
            rows="3"
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleSkip}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold transition"
          >
            {t('classroom.ratingSkip')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={rating === 0}
            className={`flex-1 py-3 rounded-lg font-semibold transition ${
              rating === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
            }`}
          >
            {t('classroom.ratingSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatingModal;
